import { createFileRoute } from '@tanstack/react-router';
import { Button, Container, Stack, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useSuspenseQuery } from '@apollo/client';
import { myCompanyDocument } from '@social-garden/api/documents/company.ts';
import Section from '@/components/Section.tsx';
import useCampaignUsage from '@/hooks/useCampaignUsage.ts';
import useBrandUsage from '@/hooks/useBrandUsage.ts';
import SubscriptionPlanItem from '@/components/SubscriptionPlanItem.tsx';
import PageTitle from '@/components/PageTitle.tsx';
import useStorageSpaceUsage from '@/hooks/useStorageSpaceUsage.ts';
import CampaignUsageProgress from '@/components/CampaignUsageProgress.tsx';
import BrandUsageProgress from '@/components/BrandUsageProgress.tsx';
import StorageSpaceUsageProgress from '@/components/StorageSpaceUsageProgress.tsx';
import ButtonLink from '@/components/ButtonLink.tsx';

export const Route = createFileRoute('/_manager/_app/subscription')({
  component: Subscription,
});

function Subscription() {
  const { t } = useTranslation(['common', 'manager']);
  const {
    data: { myCompany },
  } = useSuspenseQuery(myCompanyDocument);
  const { pathname } = useLocation();

  const {
    max: maxCampaignUsage,
    current: currentCampaignUsage,
    exceeded: campaignUsageExceeded,
  } = useCampaignUsage();
  const {
    max: maxBrandUsage,
    current: currentBrandUsage,
    exceeded: brandUsageExceeded,
  } = useBrandUsage();
  const {
    max: maxStorageSpaceUsage,
    current: currentStorageSpaceUsage,
    exceeded: storageSpaceUsageExceeded,
  } = useStorageSpaceUsage();

  const returnUrl = useMemo(
    () => window.location.origin + pathname,
    [pathname],
  );

  return (
    <Container>
      <Grid container spacing={4}>
        <Grid size={12}>
          <PageTitle>{t('manager:subscription.title')}</PageTitle>
        </Grid>
        {myCompany.currentSubscription?.subscriptionPlan ? (
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}>
            <SubscriptionPlanItem
              sx={{
                borderColor: (theme) => theme.palette.primary.main,
              }}
              subscriptionPlan={myCompany.currentSubscription.subscriptionPlan}
              disablePriceHighlighting
            />
          </Grid>
        ) : null}
        <Grid
          size={{
            xs: 12,
            md: 'grow',
          }}>
          <Stack spacing={4}>
            <Section header={t('manager:subscription.header.usage')}>
              <CampaignUsageProgress
                current={currentCampaignUsage}
                max={maxCampaignUsage}
              />
              <BrandUsageProgress
                current={currentBrandUsage}
                max={maxBrandUsage}
              />
              <StorageSpaceUsageProgress
                current={currentStorageSpaceUsage}
                max={maxStorageSpaceUsage}
              />
              <Button
                fullWidth
                variant="contained"
                size={
                  campaignUsageExceeded ||
                  brandUsageExceeded ||
                  storageSpaceUsageExceeded
                    ? 'large'
                    : 'medium'
                }
                href={`mailto:${import.meta.env.VITE_SALES_EMAIL}`}>
                {t('common:contactSales')}
              </Button>
            </Section>
            <ButtonLink
              fullWidth
              variant="outlined"
              to="/customer-portal"
              search={{
                returnUrl,
              }}>
              {t('manager:manageStripeAccountButton')}
            </ButtonLink>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
}
